<script setup lang="ts">
import { ref, watch } from 'vue'
import FieldDropdown from './FieldDropdown.vue'
import { chainFromText, namesFromText, textFromChain, textFromNames, type MemberSuggestions } from './member-names'

/**
 * A text input for one member chain or for several member names. It suggests the fields of the upstream data,
 * and takes a typed name when there is nothing to suggest. It takes no expression slots.
 */
const props = defineProps<{
  modelValue: string[]
  /** Accessible name of the field, such as "Key". */
  label: string
  placeholder?: string
  /** True for member names: commas between them, and several can be chosen. False for one member chain. */
  multiple?: boolean
  suggestions: MemberSuggestions
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const parse = (text: string) => (props.multiple ? namesFromText(text) : chainFromText(text))
const format = (value: string[]) => (props.multiple ? textFromNames(value) : textFromChain(value))

// The input keeps exactly what was typed, such as a trailing comma. It is rewritten only when the value
// becomes something the typed text does not already mean: a pick from the menu, or another service's value.
const text = ref(format(props.modelValue))
watch(
  () => props.modelValue,
  (value) => {
    const typedTextMeansValue = format(parse(text.value)) === format(value)
    if (!typedTextMeansValue) text.value = format(value)
  },
)

function onInput(event: Event, show: () => void) {
  show()
  text.value = (event.target as HTMLInputElement).value
  emit('update:modelValue', parse(text.value))
}

function isChosen(name: string): boolean {
  return props.multiple ? props.modelValue.includes(name) : textFromChain(props.modelValue) === name
}

// A single field takes the picked chain and closes. A multiple field adds the name, or removes it when it
// is already chosen, and stays open for the next one.
function choose(name: string, close: () => void) {
  if (!props.multiple) {
    emit('update:modelValue', chainFromText(name))
    close()
    return
  }
  const withoutName = props.modelValue.filter((chosen) => chosen !== name)
  const wasChosen = withoutName.length < props.modelValue.length
  emit('update:modelValue', wasChosen ? withoutName : [...props.modelValue, name])
}
</script>

<template>
  <FieldDropdown>
    <template #default="{ show }">
      <input
        type="text"
        class="form-control font-monospace"
        :value="text"
        :placeholder="placeholder"
        :aria-label="label"
        autocomplete="off"
        spellcheck="false"
        @focus="show"
        @click="show"
        @input="onInput($event, show)"
      />
    </template>

    <template #menu="{ close }">
      <p v-if="suggestions.plain" class="text-body-secondary small mb-0 px-1">
        Each element is a plain value, so there is no field to name.
      </p>
      <p v-else-if="suggestions.members.length === 0" class="text-body-secondary small mb-0 px-1">
        Nothing to suggest yet. Type the name, or run the workflow once to list the fields.
      </p>
      <div v-if="suggestions.members.length" role="listbox" :aria-label="label" :aria-multiselectable="multiple">
        <button
          v-for="member in suggestions.members"
          :key="member.name"
          type="button"
          class="dropdown-item d-flex align-items-center gap-2 px-1"
          role="option"
          :aria-selected="isChosen(member.name)"
          @click="choose(member.name, close)"
        >
          <i class="bi bi-check2 text-primary" :class="{ invisible: !isChosen(member.name) }" aria-hidden="true"></i>
          <span class="font-monospace flex-grow-1">{{ member.name }}</span>
          <span class="text-body-secondary small text-truncate">{{ member.sample }}</span>
        </button>
      </div>
    </template>
  </FieldDropdown>
</template>
